import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Phone, 
  Mail, 
  Clock,
  Shield,
  CheckCircle2,
  Users,
  DollarSign,
  Plane,
  MapPin,
  Car,
  Star,
  Award,
  Headphones,
  Globe,
  ArrowRight,
  Palmtree,
  Sparkles
} from 'lucide-react';
import { BookingWidget } from '@/components/BookingWidget';
import { useTestimonials } from '@/hooks/useTestimonials';
import { useTours } from '@/hooks/useTours';
import { TourCard } from '@/components/TourCard';
import Layout from '@/components/Layout';
import { useHeroSlides } from '@/hooks/useHeroSlides';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import bavaroBeach from '@/assets/bavaro-beach.jpg';

const HomePage = () => {
  const { testimonials } = useTestimonials();
  const { tours } = useTours();
  const { data: heroSlides, isLoading: slidesLoading } = useHeroSlides();
  const { getConfig } = useSiteConfig();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const featuredTestimonials = testimonials.filter(t => t.is_featured).slice(0, 3);
  const featuredTours = tours.filter(t => t.is_active && t.is_featured).slice(0, 4);

  useEffect(() => {
    if (heroSlides && heroSlides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [heroSlides]);

  const nextSlide = () => {
    if (heroSlides) {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }
  };

  const prevSlide = () => {
    if (heroSlides) {
      setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
    }
  };

  const airports = [
    {
      code: "PUJ",
      name: "Punta Cana International Airport",
      description: "Main gateway to Punta Cana and Bávaro",
      popular: true,
      transferTime: "15-45 min"
    },
    {
      code: "SDQ",
      name: "Las Américas International Airport",
      description: "Serving Santo Domingo and surrounding areas",
      popular: false,
      transferTime: "2-3 hours"
    },
    {
      code: "LRM",
      name: "La Romana International Airport",
      description: "Gateway to La Romana and Casa de Campo",
      popular: false,
      transferTime: "45-60 min"
    },
    {
      code: "AZS",
      name: "Samaná El Catey International Airport",
      description: "Access to Samaná Peninsula",
      popular: false,
      transferTime: "2.5-3 hours"
    },
    {
      code: "POP",
      name: "Puerto Plata International Airport",
      description: "North coast access",
      popular: false,
      transferTime: "3.5-4 hours"
    },
    {
      code: "STI",
      name: "Cibao International Airport",
      description: "Santiago and Cibao region",
      popular: false,
      transferTime: "4+ hours"
    }
  ];

  if (slidesLoading) {
    return <Layout><div className="min-h-screen flex items-center justify-center">Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Hero Section with Dynamic Slider */}
        <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden">
          {/* Background Slides */}
          {heroSlides?.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url(${slide.image_url})` }}
            >
              <div className="absolute inset-0 bg-gradient-overlay" />
            </div>
          ))}
          
          {/* Navigation Arrows */}
          {heroSlides && heroSlides.length > 1 && (
            <>
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all duration-300 hover:scale-110"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all duration-300 hover:scale-110"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Slide Indicators */}
          {heroSlides && heroSlides.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-secondary scale-125' : 'bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}
          
          <div className="relative z-10 container mx-auto px-4 py-20">
            <div className="text-center text-white mb-12 animate-fade-in">
              <Badge className="mb-6 bg-secondary hover:bg-secondary/90 text-secondary-foreground border-secondary backdrop-blur-sm text-lg px-8 py-3 shadow-xl font-bold">
                Premium Transfer & Tour Service
              </Badge>
              
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight drop-shadow-2xl">
                Your Premier Transfer & Tour Service
                <br />
                <span className="text-secondary">in Punta Cana</span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto drop-shadow-lg font-medium">
                Experience the Caribbean with Comfort & Style
              </p>
            </div>

            {/* Transfer Booking Form */}
            <div className="max-w-5xl mx-auto animate-scale-in">
              <BookingWidget />
            </div>
          </div>
        </section>

        {/* Featured Tours Section */}
        {featuredTours.length > 0 && (
          <section className="py-24 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16 animate-fade-in">
                <Badge variant="secondary" className="mb-4 text-base px-6 py-2">{getConfig('home_tours_badge', 'Popular Tours')}</Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{getConfig('home_tours_title', 'Discover Punta Cana')}</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  {getConfig('home_tours_description', 'Explore the most amazing tours and experiences in the Dominican Republic')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                {featuredTours.map((tour) => (
                  <TourCard key={tour.id} tour={tour} />
                ))}
              </div>

              <div className="text-center">
                <Link to="/tours">
                  <Button size="lg" variant="outline" className="group">
                    View All Tours
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Who We Are Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 animate-fade-in">
                <Badge variant="secondary" className="text-base px-6 py-2">{getConfig('home_about_badge', 'About Us')}</Badge>
                <h2 className="text-4xl md:text-5xl font-bold">{getConfig('home_about_title', 'Who We Are')}</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {getConfig('home_about_description_1', 'Dominican Sol Transfers is your exclusive transfer and tour service in Punta Cana and throughout the Dominican Republic. We combine professional service with local expertise to create unforgettable Caribbean experiences.')}
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {getConfig('home_about_description_2', 'Our team of experienced, bilingual drivers and tour guides ensure your journey is comfortable, safe, and memorable. Whether you need airport transfers or exciting tours, we\'re here to make your Dominican vacation exceptional.')}
                </p>
                <Link to="/about">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group">
                    {getConfig('home_about_button_text', 'Learn More About Us')}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-6 animate-scale-in">
                <Card className="text-center p-6 hover:shadow-lg transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">500+</h3>
                  <p className="text-muted-foreground">Happy Customers</p>
                </Card>

                <Card className="text-center p-6 hover:shadow-lg transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="h-8 w-8 text-accent" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">10+</h3>
                  <p className="text-muted-foreground">Years Experience</p>
                </Card>

                <Card className="text-center p-6 hover:shadow-lg transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Headphones className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">24/7</h3>
                  <p className="text-muted-foreground">Support Available</p>
                </Card>

                <Card className="text-center p-6 hover:shadow-lg transition-all hover:scale-105">
                  <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">100%</h3>
                  <p className="text-muted-foreground">Safe & Insured</p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-in">
              <Badge variant="secondary" className="mb-4 text-base px-6 py-2">{getConfig('home_why_badge', 'Our Advantages')}</Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">{getConfig('home_why_title', 'Why Choose Us')}</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {getConfig('home_why_description', 'Experience the difference with Dominican Sol Transfers')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="p-8 hover:shadow-xl transition-all hover:scale-105 group">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Professional Drivers</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Bilingual, licensed, and experienced drivers who know Punta Cana inside and out.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all hover:scale-105 group">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary/20 transition-colors">
                  <Car className="h-8 w-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Modern Fleet</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Clean, comfortable, and air-conditioned vehicles for your perfect journey.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all hover:scale-105 group">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                  <Headphones className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-2xl font-bold mb-4">24/7 Support</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Always available for your needs, day or night, 365 days a year.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all hover:scale-105 group">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-secondary/20 transition-colors">
                  <DollarSign className="h-8 w-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Best Prices</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Competitive rates with no hidden fees. What you see is what you pay.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all hover:scale-105 group">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Easy Booking</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Simple online reservation system with instant confirmation.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all hover:scale-105 group">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                  <Shield className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Safety First</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Fully insured and certified for your peace of mind and safety.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Our Services Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-in">
              <Badge variant="secondary" className="mb-4 text-base px-6 py-2">{getConfig('home_services_badge', 'What We Offer')}</Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">{getConfig('home_services_title', 'Our Services')}</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {getConfig('home_services_description', 'Comprehensive transportation and tour services for your perfect vacation')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="overflow-hidden group hover:shadow-2xl transition-all">
                <div className="relative h-80">
                  <img 
                    src={bavaroBeach} 
                    alt="Airport Transfers"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
                      <Plane className="h-8 w-8" />
                    </div>
                    <h3 className="text-3xl font-bold mb-3">{getConfig('home_services_transfer_title', 'Airport Transfers')}</h3>
                    <p className="text-white/90 mb-6 leading-relaxed">
                      {getConfig('home_services_transfer_description', 'Reliable transportation from/to all Dominican airports. Door-to-door service with flight tracking and meet & greet.')}
                    </p>
                    <Link to="/transfers">
                      <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group">
                        Book Transfer
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>

              <Card className="overflow-hidden group hover:shadow-2xl transition-all">
                <div className="relative h-80">
                  <img 
                    src={bavaroBeach} 
                    alt="Tours & Excursions"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-accent via-accent/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
                      <Palmtree className="h-8 w-8" />
                    </div>
                    <h3 className="text-3xl font-bold mb-3">{getConfig('home_services_tours_title', 'Tours & Excursions')}</h3>
                    <p className="text-white/90 mb-6 leading-relaxed">
                      {getConfig('home_services_tours_description', 'Discover the beauty of the Dominican Republic with our exciting tours and excursions to the best attractions.')}
                    </p>
                    <Link to="/tours">
                      <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group">
                        Explore Tours
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Dominican Republic Airports Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-in">
              <Badge variant="secondary" className="mb-4 text-base px-6 py-2">{getConfig('home_airports_badge', 'We Serve All Airports')}</Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">{getConfig('home_airports_title', 'Dominican Republic Airports')}</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {getConfig('home_airports_description', 'Professional transfer service from and to all major Dominican Republic airports')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {airports.map((airport) => (
                <Card key={airport.code} className={`p-6 hover:shadow-xl transition-all hover:scale-105 ${airport.popular ? 'border-2 border-secondary' : ''}`}>
                  {airport.popular && (
                    <Badge className="mb-4 bg-secondary hover:bg-secondary/90">Most Popular</Badge>
                  )}
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Plane className="h-7 w-7 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge variant="outline" className="font-bold">{airport.code}</Badge>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{airport.transferTime}</span>
                      </div>
                      <h3 className="text-lg font-bold mb-2">{airport.name}</h3>
                      <p className="text-sm text-muted-foreground">{airport.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link to="/transfers">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group">
                  Book Your Transfer Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {featuredTestimonials.length > 0 && (
          <section className="py-24 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16 animate-fade-in">
                <Badge variant="secondary" className="mb-4 text-base px-6 py-2">Client Reviews</Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">What Our Customers Say</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Real experiences from travelers who trusted us with their journey
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {featuredTestimonials.map((testimonial) => (
                  <Card key={testimonial.id} className="p-8 hover:shadow-xl transition-all hover:scale-105">
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`h-5 w-5 ${i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-6 italic">
                      "{testimonial.comment}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-bold text-lg">
                          {testimonial.customer_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-bold">{testimonial.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16 animate-fade-in">
                <Badge variant="secondary" className="mb-4 text-base px-6 py-2">{getConfig('home_faq_badge', 'Help Center')}</Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{getConfig('home_faq_title', 'Frequently Asked Questions')}</h2>
                <p className="text-xl text-muted-foreground">
                  {getConfig('home_faq_description', 'Everything you need to know about our services')}
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    How do I book a transfer?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Simply use our booking form above, select your pickup and destination, choose the number of passengers, and click "Search Available Transfers". You can then select your preferred vehicle type and complete the booking online.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    What's included in the transfer price?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    All our transfers include door-to-door service, professional bilingual driver, meet & greet at the airport, flight tracking, all taxes, and tolls. There are no hidden fees.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    Do you offer child seats?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Yes, we provide child seats and booster seats upon request at no additional charge. Please inform us when making your reservation about the number and type of child seats you need.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    Can I cancel or modify my booking?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Yes, you can cancel or modify your booking up to 24 hours before your scheduled pickup time for a full refund. Please contact our customer service team for assistance.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    How far in advance should I book?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    We recommend booking at least 24-48 hours in advance to ensure availability, especially during peak season. However, we also accept last-minute bookings subject to availability.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    Do drivers speak English?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Yes, all our drivers are bilingual and speak both English and Spanish fluently. We can also arrange drivers who speak other languages upon request.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-7" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    What if my flight is delayed?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    We track all flights in real-time, so if your flight is delayed, we'll adjust your pickup time accordingly at no extra charge. Your driver will be there waiting for you whenever you arrive.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8" className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:text-primary">
                    Are there extra charges for luggage?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    No, standard luggage is included in the price. Each passenger is allowed one large suitcase and one carry-on. For excessive or oversized luggage, please contact us in advance.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="text-center mt-12">
                <p className="text-muted-foreground mb-4">Still have questions?</p>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="group">
                    Contact Us
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section 
          className="relative py-32 bg-cover bg-center"
          style={{ backgroundImage: `url(${bavaroBeach})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/90 to-accent/95" />
          
          <div className="relative z-10 container mx-auto px-4 text-center text-white">
            <Sparkles className="h-16 w-16 mx-auto mb-6 text-secondary animate-float" />
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Ready to Book Your Transfer?
            </h2>
            <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-white/90">
              Experience comfort and reliability with Dominican Sol Transfers.<br />
              Your Caribbean adventure starts here.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/transfers">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-12 py-6 h-auto group">
                  Book Transfer Now
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 text-lg px-12 py-6 h-auto backdrop-blur-sm">
                  Contact Us
                </Button>
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center text-white/90">
              <div className="flex items-center space-x-2">
                <Phone className="h-5 w-5" />
                <span className="font-medium">+1 (809) 123-4567</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-5 w-5" />
                <span className="font-medium">info@dominicansol.com</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="h-5 w-5" />
                <span className="font-medium">24/7 Available</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
